// 05-11 (auditoría, QA M2/M3) — la PILA de Escape de la oficina: un Escape cierra UNA cosa, la de más arriba.
//
// Antes cada pieza escuchaba Escape por su cuenta en `window` y adivinaba si había algo encima mirando el DOM
// (`[role="dialog"]`). Fallaba por los dos lados: un Escape cerraba a la vez el chat y «Ordenar» (el chat no es un
// diálogo, así que «Ordenar» no lo veía), y en la hoja del PDF el primer Escape se lo quedaba la pista (Radix Tooltip)
// del botón con el foco: hacían falta dos. Ahora quien se puede cerrar con Escape se apunta aquí mientras está abierto,
// con su nivel; un único oyente reparte la tecla al de arriba (nivel más alto; a igual nivel, el último que se abrió).
//
// Dos clases de capa, por cómo conviven con lo de dentro:
// - `hoja` (diálogos de Radix: Saltar, la vista previa, las ventanas): se atiende en la fase de CAPTURA, antes que Radix,
//   para que una pista abierta no se quede la tecla. Lo de Radix que se abre DENTRO de una hoja (un menú, una lista
//   desplegable, otro diálogo que no se ha apuntado) sigue siendo suyo: con algo así abierto la pila no actúa.
// - `panel` (lo que no es de Radix: el chat, «Ordenar», la Sala): en la fase de BURBUJA, después de los campos de dentro
//   (el buscador del chat, el nombre de un grupo, la grabación de voz…), que marcan la tecla como usada si la gastan.
// La capa de arriba puede declinar (devolver `false`: p. ej. el chat con texto en la caja): entonces no se cierra nada
// más abajo — un Escape nunca cierra lo de debajo de algo que sigue abierto.
import { useEffect, useRef } from 'react';

export type ClaseCapa = 'hoja' | 'panel';

/** Niveles: lo de más arriba gana. «Ordenar» está por debajo de cualquier ventana; las hojas, por encima de todo. */
export const NIVEL_ESCAPE = { ordenar: 0, ventana: 1, hoja: 2 } as const;

interface Capa {
  clase: ClaseCapa;
  nivel: number;
  orden: number;
  id: string | undefined;
  /** Sin evento = la cierra otra pieza (cerrarHojas), no una tecla. */
  alEscape: (e?: KeyboardEvent) => boolean | void;
}

const capas = new Set<Capa>();
let contador = 0;

/** La capa de arriba (null = ninguna). */
export function capaDeArriba(): Capa | null {
  let arriba: Capa | null = null;
  for (const c of capas) if (!arriba || c.nivel > arriba.nivel || (c.nivel === arriba.nivel && c.orden > arriba.orden)) arriba = c;
  return arriba;
}

/** Marca de las capas apuntadas: lo de Radix que NO la lleva (menús, listas, diálogos sueltos) se atiende solo. */
export const MARCA_CAPA = 'data-capa-escape';
const AJENAS = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]';

function hayCapaAjena(): boolean {
  return [...document.querySelectorAll(AJENAS)].some((el) => !el.closest(`[${MARCA_CAPA}]`));
}

/** ¿Hay un diálogo, un menú o una lista desplegable abiertos (apuntados o no)? Sus teclas son suyas. */
export function hayDialogoAbierto(): boolean {
  return !!document.querySelector(AJENAS);
}

function repartir(e: KeyboardEvent, fase: ClaseCapa): void {
  if (e.key !== 'Escape' || e.isComposing || e.defaultPrevented) return;
  const arriba = capaDeArriba();
  if (!arriba || arriba.clase !== fase || hayCapaAjena()) return;
  if (arriba.alEscape(e) === false) return;
  e.preventDefault();
  // En captura, que no llegue a Radix (la pista de un botón se lo quedaría).
  if (fase === 'hoja') e.stopPropagation();
}

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e) => repartir(e, 'hoja'), true);
  window.addEventListener('keydown', (e) => repartir(e, 'panel'));
}

/**
 * Cierra todas las hojas abiertas (de arriba abajo) salvo la `excepto`: «Saltar a…» lo hace al elegir, para que el chat
 * elegido se vea y lo que Pau escriba vaya a la caja que ve (QA G4: con el PDF de otro agente a pantalla completa encima,
 * se escribía a ciegas en el chat de debajo).
 */
export function cerrarHojas(excepto: string): void {
  const hojas = [...capas].filter((c) => c.clase === 'hoja' && c.id !== excepto).sort((a, b) => b.nivel - a.nivel || b.orden - a.orden);
  for (const h of hojas) h.alEscape();
}

/**
 * Apunta una capa en la pila mientras `activa`. `alEscape` puede devolver `false` para no gastar la tecla (y entonces
 * nada de debajo se cierra). Se apunta al activarse y no al cambiar `alEscape` (se lee siempre el último): volver a
 * pintarse no la sube encima de lo que se abrió después.
 */
export function useEscape(activa: boolean, alEscape: (e?: KeyboardEvent) => boolean | void, { clase = 'hoja', nivel, id }: { clase?: ClaseCapa; nivel?: number; id?: string } = {}): void {
  const ultimo = useRef(alEscape);
  ultimo.current = alEscape;
  const nivelFinal = nivel ?? (clase === 'hoja' ? NIVEL_ESCAPE.hoja : NIVEL_ESCAPE.ventana);
  useEffect(() => {
    if (!activa) return;
    contador += 1;
    const capa: Capa = { clase, nivel: nivelFinal, orden: contador, id, alEscape: (e) => ultimo.current(e) };
    capas.add(capa);
    return () => {
      capas.delete(capa);
    };
  }, [activa, clase, nivelFinal, id]);
}

/** ¿Está escribiendo en un campo con texto? (el chat y lo de Santi no se cierran entonces con Escape). */
export function escribiendoConTexto(e: KeyboardEvent | undefined): boolean {
  const destino = (e?.target ?? null) as HTMLElement | null;
  if (!destino) return false;
  if (destino.isContentEditable) return !!destino.textContent;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(destino.tagName) && !!(destino as HTMLInputElement).value;
}
